import React, { PureComponent } from 'react';

import DataGrid, { ColumnChooser, Editing, Grouping, GroupPanel, Paging, Scrolling, SearchPanel, Sorting } from 'devextreme-react/data-grid';

import SelectBox from 'devextreme-react/select-box';
import { Tooltip } from 'devextreme-react/tooltip';

import EnergyCellPopOutEditor from '../editors/energy-cell-popout/EnergyCellPopOutEditor';

import notify from 'devextreme/ui/notify';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';

import ScheduleApi from '../api-services/ScheduleApi';

import AsyncUtils from '../common/utils/AsyncUtils';
import DateTimeUtils from '../common/utils/DateTimeUtils';
import CoreUtils from '../common/utils/CoreUtils';
import DomUtils from '../common/utils/DomUtils';
import GridUtils from '../common/utils/GridUtils';
import ReactUtils from '../common/utils/ReactUtils';
import StringUtils from '../common/utils/StringUtils';

import { DateViewMode } from '../common/value-types/DateViewMode';

import PreferenceManager from '../application/PreferenceManager';

import ScheduleCommandDataModel from './ScheduleCommandDataModel';
import ScheduleDataModel, { hlhEntryField, llhEntryField, atcEntryField, hlhCode, llhCode, atcCode } from './ScheduleDataModel';
import ScheduleSideBar from './ScheduleSideBar';

import ContractLimitsView from './contract-limits/ContractLimitsView';

import MemoPaneDataModel from '../memos/MemoPaneDataModel';
import MemoPaneView from '../memos/MemoPaneView';

import ScheduleDateContextView from './views/ScheduleDateContextView';

import ScheduleDateRolloverController from './controllers/ScheduleDateRolloverController';
import ScheduleToolTipController from './controllers/ScheduleToolTipController';

import ScheduleDateRangePopup from '../popups/schedule-date-range/ScheduleDateRangePopup';
import ScheduleDateRangeDataModel from '../popups/schedule-date-range/ScheduleDateRangeDataModel';

import ManageColumnDefaultsPopup from '../popups/manage-column-defaults/ManageColumnDefaultsPopup';
import ManageColumnDefaultsDataModel from '../popups/manage-column-defaults/ManageColumnDefaultsDataModel';

import TableauView from '../tableau/TableauView';
import TableauNavigation from '../tableau/TableauNavigation';

import EnergyValue from '../common/value-types/EnergyValue';

import './ScheduleMain.css';

const DateRangePopupMode_Resubmit = 'Resubmit';
const DateRangePopupMode_Recalculate = 'Recalculate';

const TabType_DateContextView = 'contract-view';
const TabType_TableauView = 'tableau-view';

export default class ScheduleMain extends PureComponent {
  constructor(props) {
    super(props);

    this.session = this.props.session;
    this.locale = this.props.session.locale;
    this.counterparty = this.props.counterparty;
    this.timeZoneShortName = this.props.session.timeZoneShortName;
    this.timeZoneLongName = this.props.session.timeZoneLongName;

    this.hasDataGridInitiallyLoaded = false;
    this.toolTipDelayMs = 1000;
    this.isGroupPanelVisible = true;
    this.dataGridEmptyMessage = '';

    this.memoPaneDataModel = new MemoPaneDataModel(this.locale, this.counterparty);

    this.commandDataModel = new ScheduleCommandDataModel(this.counterparty.preferenceGroupName);
    this.dateRolloverController = new ScheduleDateRolloverController(this.commandDataModel, this.props.counterparty);

    this.dateRangeDataModel = new ScheduleDateRangeDataModel();

    this.manageColumnDefaultsDataModel = new ManageColumnDefaultsDataModel();

    this.fullRefreshListener = this.onFullRefresh.bind(this);
    this.backgroundRefreshListener = this.onBackgroundRefresh.bind(this);
    this.showManageColumnPreferencesListener = this.onShowManageColumnPreferences.bind(this);
    this.updateSchedulesListener = this.onUpdateSchedules.bind(this);
    this.gridFiltersChangedListener = this.onGridFiltersChanged.bind(this);
    this.dateRollingOverListener = this.onDateRollingOver.bind(this);
    this.submitChangesListener = this.onSubmitChanges.bind(this);
    this.revertChangesListener = this.onRevertChanges.bind(this);
    this.exportToExcelListener = this.onExportToExcel.bind(this);
    this.resubmitValuesListener = this.onResubmitValues.bind(this);
    this.recalculateValuesListener = this.onRecalculateValues.bind(this);

    this.dataGridScrollable = null;
    this.onDataGridScrollListener = this.onDataGridScroll.bind(this);

    this.dataGridRef = React.createRef();
    this.popOutEditorRef = React.createRef();
    this.tooltipRef = React.createRef();

    this.usePopOutEditor = true;

    this.toolTipController = new ScheduleToolTipController(this.tooltipRef);

    this.viewKeyDownHandler = this.onViewKeyDown.bind(this);

    this.isAltRowIndex = {};
    this.scheduleRowIndex = {};
    this.currentEdit = null;

    this.parentContainerElement = DomUtils.getElementItemByClassName('pwx-main-container');
    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.onResizeObserverEvent()));
    this.resizeObserver.observe(this.parentContainerElement);

    const datacenterState = this.intializeDatacenterTabsState();
    this.state = {
      ...datacenterState,
      ...this.intializeViewTabsState(datacenterState.currentDatacenterTab)
    };
    this.isSwitchingViews = false;
  }

  componentDidMount() {
    this.dataModel.setScheduleFilter(this.state.currentViewTab?.filter);

    if (this.state.currentDatacenterTab != null)
      this.dataModel.setDatacenterFilter(this.state.currentDatacenterTab.displayName);

    this.commandDataModel.addEventListener('fullRefresh', this.fullRefreshListener);
    this.commandDataModel.addEventListener('backgroundRefresh', this.backgroundRefreshListener);
    this.commandDataModel.addEventListener('showManageColumnPreferences', this.showManageColumnPreferencesListener);
    this.commandDataModel.addEventListener('updateSchedules', this.updateSchedulesListener);
    this.commandDataModel.addEventListener('gridFiltersChanged', this.gridFiltersChangedListener);
    this.commandDataModel.addEventListener('dateRollingOver', this.dateRollingOverListener);
    this.commandDataModel.addEventListener('submitChanges', this.submitChangesListener);
    this.commandDataModel.addEventListener('revertChanges', this.revertChangesListener);
    this.commandDataModel.addEventListener('exportToExcel', this.exportToExcelListener);
    this.commandDataModel.addEventListener('resubmitValues', this.resubmitValuesListener);
    this.commandDataModel.addEventListener('recalculateValues', this.recalculateValuesListener);

    this.updateViewHeight();
    this.attachKeyDownEvent();

    this.dateRolloverController.activate();
    this.enactGridFilters()
      .then(() => this.refreshGridValues());

    PreferenceManager.updateValue(
      PreferenceManager.Configuration,
      PreferenceManager.SelectedCounterparty,
      this.counterparty.code
    );
  }

  componentWillUnmount() {
    this.closePopOutEditor();

    this.dateRolloverController.deactivate();

    this.resizeObserver.disconnect();
    this.resizeObserver = null;
    this.parentContainerElement = null;
    this.detachKeyDownEvent();
    this.toolTipController.dispose();
    this.disposePopOutEditor();
    this.dataModel.shutdown();

    this.dateRangeDataModel.dispose();

    this.commandDataModel.removeEventListener('recalculateValues', this.recalculateValuesListener);
    this.commandDataModel.removeEventListener('resubmitValues', this.resubmitValuesListener);
    this.commandDataModel.removeEventListener('exportToExcel', this.exportToExcelListener);
    this.commandDataModel.removeEventListener('revertChanges', this.revertChangesListener);
    this.commandDataModel.removeEventListener('submitChanges', this.submitChangesListener);
    this.commandDataModel.removeEventListener('dateRollingOver', this.dateRollingOverListener);
    this.commandDataModel.removeEventListener('gridFiltersChanged', this.gridFiltersChangedListener);
    this.commandDataModel.removeEventListener('updateSchedules', this.updateSchedulesListener);
    this.commandDataModel.removeEventListener('showManageColumnPreferences', this.showManageColumnPreferencesListener);
    this.commandDataModel.removeEventListener('backgroundRefresh', this.backgroundRefreshListener);
    this.commandDataModel.removeEventListener('fullRefresh', this.fullRefreshListener);
    this.commandDataModel.dispose();

    this.memoPaneDataModel.dispose();
  }

  componentDidUpdate() {
    this.updateDataGridEmptyMessage();
  }

  getDataGridRowClassName() {
    return 'pwx-schedule-datagrid-row';
  }

  async loadGridValues() { }
  async updateGridValues(lastRetrievalTimeUtc) { }
  submitGridChanges(onSubmitFinished) { }
  revertGridChanges() { }
  onViewKeyDown(event) { }

  render() {
    return (
      <div className="pwx-schedule-view">
        <ScheduleDateRangePopup
          locale={this.locale}
          dataModel={this.dateRangeDataModel}
          onContinue={this.onDateRangePopupContinue.bind(this)}
          onCancel={this.onDateRangePopupCancel.bind(this)}
        />
        <ManageColumnDefaultsPopup
          locale={this.locale}
          dataModel={this.manageColumnDefaultsDataModel}
          onCancel={this.onManageColumnDefaultsPopupCancel.bind(this)}
        />
        <table className="pwx-schedule-view-layout"><tbody>
          <tr>
            <td>
              {this.renderHeaderToolbar()}
            </td>
            {this.state.showDateContextView
              && (
              <td className="pwx-datebox-header-container">
                <ScheduleDateContextView dataModel={this.commandDataModel} />
                </td>
              )
            }
          </tr>
          {this.state.showDateContextView
            && (
            <tr>
                <td className="pwx-datagrid-column">
                  {this.renderScheduleGrid()}
                </td>
                <td className="pwx-summary-column">
                  <ScheduleSideBar
                    dataModel={this.commandDataModel}
                    locale={this.locale}
                    renderUsageSummaries={this.renderUsageSummaries.bind(this)}
                    renderContractLimitsView={this.renderContractLimitsView.bind(this)}
                    renderMemoView={this.renderMemoView.bind(this)}
                  />
                </td>
            </tr>
            )}
        </tbody></table>

        {this.state.showTableauView && this.renderTableauView()}

        <EnergyCellPopOutEditor
          ref={this.popOutEditorRef}
          commandDataModel={this.commandDataModel}
          dataModel={this.dataModel}
          onFocusBackward={this.onPopOutEditorFocusBackward.bind(this)}
          onFocusForward={this.onPopOutEditorFocusForward.bind(this)}
          onResolvePivotElements={this.onPopOutEditorResolvePivotElements.bind(this)}
        />
        <Tooltip
          deferRendering={false}
          closeOnOutsideClick={true}
          contentRender={this.toolTipController.render.bind(this.toolTipController)}
          ref={this.tooltipRef}
          wrapperAttr={{ class: 'pwx-tooltip-wrapper' }}
        />
      </div>
    );
  }

  renderScheduleGrid() {
    return (
      <DataGrid
        allowColumnReordering={false}
        columns={this.columnManager.getColumns()}
        dataSource={this.dataModel.viewSchedules}
        elementAttr={{
          'data-qa': this.counterparty.shortName
        }}
        headerFilter={{
          visible: true,
          texts: {
            cancel: 'Cancel',
            emptyValue: '(Empty)',
            ok: 'Apply'
          }
        }}
        keyExpr={ScheduleDataModel.scheduleIdProperty}
        noDataText=""
        onCellHoverChanged={this.onDataGridCellHoverChanged.bind(this)}
        onCellPrepared={this.onDataGridCellPrepared.bind(this)}
        onContextMenuPreparing={this.onDataGridContextMenuPreparing.bind(this)}
        onContentReady={this.onDataGridContentReady.bind(this)}
        onEditingStart={this.onDataGridEditingStart.bind(this)}
        onEditCanceling={this.onDataGridEditCanceling.bind(this)}
        onKeyDown={this.onDataGridKeyDown.bind(this)}
        onInitialized={this.onDataGridInitialized.bind(this)}
        onRowPrepared={this.onDataGridRowPrepared.bind(this)}
        onOptionChanged={this.onDataGridOptionChanged.bind(this)}
        onRowUpdated={this.onDataGridRowUpdated.bind(this)}
        onRowUpdating={this.onDataGridRowUpdating.bind(this)}
        ref={this.dataGridRef}
        repaintChangesOnly={true}
        rowAlternationEnabled={false}
        showBorders={true}
        showRowLines={true}
      >
        <ColumnChooser enabled={true} />
        <GroupPanel visible={this.isGroupPanelVisible} />
        <SearchPanel visible={true} highlightCaseSensitive={true} />
        <Grouping autoExpandAll={true} />
        <Sorting mode="multiple" />
        <Paging enabled={false} />
        <Scrolling mode="default" showScrollbar="always" />

        <Editing mode="cell" allowUpdating={true} allowAdding={false} allowDeleting={false} selectTextOnEditStart={true} />
      </DataGrid>
    );
  }

  renderHeaderToolbar() {
    return (
      <table className="pwx-header-toolbar"><tbody>
        {this.state.datacenterTabs.length > 0 && (
          <tr>
            <td className="pwx-header-toolbar-title">
              Datacenter:
            </td>
            {this.state.datacenterTabs.map(datacenterTab => (
              <td
                valign="center"
                key={datacenterTab.displayName}
                className={this.getDatacenterTabClassName(datacenterTab)}
                onClick={this.onClickDatacenterTab.bind(this, datacenterTab)}
              >{datacenterTab.displayName}
              </td>
            ))}
            <td className="pwx-header-toolbar-spring-field" />
          </tr>
        )}
        <tr>
          <td className="pwx-header-toolbar-title">
            Energy Profile: {this.counterparty.displayName}
          </td>
          {this.state.viewTabs.map(viewTab => {
            if (this.counterparty.parameters?.TableauNavigationUrl && viewTab.tabType === TabType_TableauView)
              return null;
            return (
              <td
                valign="center"
                key={viewTab.id}
                className={this.getToolbarTabClassName(viewTab)}
                id={viewTab.id}
                onClick={this.onClickViewTab.bind(this, viewTab)}
              >{viewTab.displayName}
              </td>
            );
          })}
          {
            this.counterparty.parameters?.TableauNavigationUrl && this.state.showTableauView && this.state.currentTableauPage !== null
              ? (
                <td
                  valign="center"
                  className="pwx-header-toolbar-section pwx-toolbar-tab-active"
                  onClick={this.onTableauNavigateBack.bind(this)}
                  style={{cursor: 'pointer'}}
                >Back to Dashboard List
                </td>
              )
              : null
          }
          <td className="pwx-header-toolbar-spring-field" />
          <td
            valign="center"
            className="pwx-header-toolbar-dropdown-frame"
          >
          {
            this.state.tableauPages.length > 1 && !this.counterparty.parameters?.TableauNavigationUrl
                ? (
                    <SelectBox
                      className="pwx-select-box"
                      dataSource={this.state.tableauPages}
                      displayExpr="pageTitle"
                      disabled={false}
                      dropDownOptions={{}}
                      height="24px"
                      showClearButton={false}
                      value={this.state.currentTableauPage}
                      onValueChanged={this.onSelectTableauPage.bind(this)}
                    />
                )
                : (<span />)
            }
          </td>
        </tr>
      </tbody></table>
    );
  }

  renderUsageSummaries() {
    return (<div />);
  }

  renderTableauView() {
    const hasNavigationUrl = !!this.counterparty.parameters?.TableauNavigationUrl;

    if (hasNavigationUrl) {
      if (this.state.currentTableauPage !== null) {
        return (
          <div className="pwx-tableau-view-container">
            <TableauView
              counterpartyCode={this.counterparty.code}
              script={this.state.currentViewTab?.tableauScriptUrl}
              url={this.state.currentTableauPage.pageUrl}
            />
          </div>
        );
      }

      return (
        <div className="pwx-tableau-view-container">
          <TableauNavigation
            navigationUrl={'sessionservice/tableau-navigation-page?counterpartyCode=' + encodeURIComponent(this.counterparty.code)}
            onNavigate={this.onTableauNavigate.bind(this)}
          />
        </div>
      );
    }

    return this.state.currentTableauPage !== null
      ? (
        <div className="pwx-tableau-view-container">
          <TableauView
            counterpartyCode={this.counterparty.code}
            script={this.state.currentViewTab?.tableauScriptUrl}
            url={this.state.currentTableauPage.pageUrl}
          />
        </div>
      )
      : (<div />);
  }

  renderContractLimitsView() {
    return (
      <ContractLimitsView dataModel={this.commandDataModel} locale={this.locale} />
    );
  }

  renderMemoView() {
    return (
      <MemoPaneView dataModel={this.memoPaneDataModel} locale={this.locale} />
    );
  }

  resolveDataGrid() {
    return ReactUtils.resolveReference(this.dataGridRef);
  }

  getToolbarTabClassName(viewTab) {
    if (viewTab === this.state.currentViewTab)
      return 'pwx-header-toolbar-section pwx-toolbar-tab-active';
    else if (viewTab.isEnabled)
      return 'pwx-header-toolbar-section pwx-toolbar-tab-normal';
    else
      return 'pwx-header-toolbar-section pwx-toolbar-tab-disabled';
  }

  getDatacenterTabClassName(datacenterTab) {
    if (datacenterTab === this.state.currentDatacenterTab)
      return 'pwx-header-toolbar-section pwx-datacenter-tab-active';
    else
      return 'pwx-header-toolbar-section pwx-datacenter-tab-normal';
  }

  intializeDatacenterTabsState() {
    const datacenters = this.counterparty.datacenters || [];

    if (datacenters.length === 0)
      return { datacenterTabs: [], currentDatacenterTab: null };

    const datacenterTabs = datacenters.map(dc => ({
      displayName: dc.displayName,
      displayOrder: dc.displayOrder,
      enableReporting: dc.enableReporting
    }));

    const currentDatacenterTab = datacenterTabs[0];

    return {
      datacenterTabs: datacenterTabs,
      currentDatacenterTab: currentDatacenterTab
    };
  }

  intializeViewTabsState(datacenterTab) {
    var viewTabs = [];

    const currentDc = datacenterTab !== undefined ? datacenterTab : this.state?.currentDatacenterTab;

    // Determine which contract views are active for the selected datacenter
    let contractViews = this.counterparty.contractViews;
    if (currentDc != null && this.dataModel && Object.keys(this.dataModel.indexedContracts).length > 0) {
      const activeFilters = new Set();
      for (const code in this.dataModel.indexedContracts) {
        const contract = this.dataModel.indexedContracts[code];
        const dcValue = contract.parameters.DatacenterAssignment || '';
        if (dcValue === currentDc.displayName || dcValue === '') {
          const viewValue = contract.parameters.ViewAssignment || 'Forecasting';
          activeFilters.add(viewValue);
        }
      }
      // Always include the first/default view (Forecasting)
      if (this.counterparty.contractViews.length > 0) {
        const defaultFilter = this.counterparty.contractViews[0].filter;
        if (defaultFilter) activeFilters.add(defaultFilter);
      }
      contractViews = this.counterparty.contractViews.filter(
        view => view.filter == null || activeFilters.has(view.filter)
      );
    }

    contractViews.forEach(category => {
      var viewTab = {};
      Object.assign(viewTab, category);

      viewTab.tabType = TabType_DateContextView;
      viewTab.isEnabled = true;
      viewTab.tableauScriptUrl = null;
      viewTab.tableauPages = [];

      viewTabs.push(viewTab);
    });

    if (this.counterparty.tableauIsVisible) {
      const datacenterReportingEnabled = currentDc != null
        ? currentDc.enableReporting
        : true;

      if (datacenterReportingEnabled) {
        viewTabs.push({
          id: 'tableau-view',
          displayName: this.counterparty.tableauSectionTitle,
          filter: null,
          tabType: TabType_TableauView,
          isEnabled: this.counterparty.tableauIsEnabled,
          tableauScriptUrl: this.counterparty.tableauScriptUrl,
          tableauPages: this.counterparty.tableauPages
        });
      }
    }

    var currentViewTab = viewTabs.length > 0
      ? viewTabs[0]
      : null;

    if (this.counterparty.parameters?.TableauNavigationUrl) {
      const tableauTab = viewTabs.find(tab => tab.tabType === TabType_TableauView);
      if (tableauTab)
        currentViewTab = tableauTab;
    }

    if (viewTabs.length < 2 && this.state?.datacenterTabs?.length === 0)
      viewTabs = [];

    return {
      viewTabs: viewTabs,
      currentViewTab: currentViewTab,
      showDateContextView: this.resolveShowDateContextView(currentViewTab),
      showTableauView: this.resolveShowTableauView(currentViewTab),
      tableauPages: this.resolveTableauPages(currentViewTab),
      currentTableauPage: this.resolveCurrentTableauPage(currentViewTab)
    };
  }

  resolveShowDateContextView(tab) {
    return tab != null && tab.tabType === TabType_DateContextView;
  }

  resolveShowTableauView(tab) {
    return tab != null && tab.tabType === TabType_TableauView;
  }

  resolveTableauPages(currentViewTab) {
    return CoreUtils.isDefinedAndNotNull(currentViewTab)
      && currentViewTab.tableauPages.length > 0
      ? currentViewTab.tableauPages
      : [];
  }

  resolveCurrentTableauPage(currentViewTab) {
    if (this.counterparty.parameters?.TableauNavigationUrl)
      return null;

    return CoreUtils.isDefinedAndNotNull(currentViewTab)
      && currentViewTab.tableauPages.length > 0
      ? currentViewTab.tableauPages[0]
      : null;
  }

  onClickViewTab(viewTab) {
    if (!viewTab.isEnabled)
      return;

    if (viewTab === this.state.currentViewTab)
      return;

    this.startOperationInProgress();

    this.isSwitchingViews = true;
    this.setProcessingSchedulesMessage();

    this.updateCurrentFilter(viewTab.filter);

    this.setState({
      currentViewTab: viewTab,
      showDateContextView: this.resolveShowDateContextView(viewTab),
      showTableauView: this.resolveShowTableauView(viewTab),
      tableauPages: this.resolveTableauPages(viewTab),
      currentTableauPage: this.resolveCurrentTableauPage(viewTab)
    });
  }

  onClickDatacenterTab(datacenterTab) {
    if (datacenterTab === this.state.currentDatacenterTab)
      return;

    this.startOperationInProgress();
    this.isSwitchingViews = true;
    this.setProcessingSchedulesMessage();

    this.dataModel.setDatacenterFilter(datacenterTab.displayName);

    var viewTabsState = this.intializeViewTabsState(datacenterTab);
    this.updateCurrentFilter(viewTabsState.currentViewTab?.filter);

    this.setState({
      currentDatacenterTab: datacenterTab,
      ...viewTabsState
    });
  }

  onSelectTableauPage(event) {
    var tableauPage = event.value;

    if (tableauPage === this.state.currentTableauPage)
      return;

    if (this.state.tableauPages.indexOf(tableauPage) === -1)
      return;

    this.setState({
      currentTableauPage: tableauPage
    });
  }

  onTableauNavigate(url) {
    this.setState({
      currentTableauPage: { pageUrl: url, pageTitle: '' }
    });
  }

  onTableauNavigateBack() {
    this.setState({
      currentTableauPage: null
    });
  }

  updateCurrentFilter(filter) {
    this.scheduleRowIndex = {};
    this.dataModel.setScheduleFilter(filter);

    var dataGrid = this.resolveDataGrid();
    if (CoreUtils.isDefinedAndNotNull(dataGrid))
      dataGrid.refresh();
  }

  startOperationInProgress() {
    this.commandDataModel.updateProperties({
      isOperationInProgress: true
    });
  }

  endOperationInProgress() {
    this.commandDataModel.updateProperties({
      isOperationInProgress: false
    });
  }

  async refreshGridValues() {
    this.commandDataModel.updateProperties({
      isOperationInProgress: true
    });

    this.toolTipController.deactivate();
    this.currentEdit = null;

    await this.loadGridValues();

    // Refresh view tabs after data loads (per-datacenter view filtering)
    if (this.state.currentDatacenterTab != null) {
      const viewTabsState = this.intializeViewTabsState(this.state.currentDatacenterTab);
      this.setState(viewTabsState);
    }

    this.columnManager.updateColumnVisibility(
      this.resolveDataGrid(),
      this.dataModel.activeDataFields
    );

    this.scheduleRowIndex = {};

    var dataGrid = this.resolveDataGrid();

    if (CoreUtils.isDefinedAndNotNull(dataGrid))
      dataGrid.refresh();

    this.hasDataGridInitiallyLoaded = true;
    
    this.setProcessingSchedulesMessage();
    this.scrollToFirstAvailableSchedule();

    this.commandDataModel.updateProperties({
      isOperationInProgress: false
    });
  }

  scrollToFirstAvailableSchedule() {
    var scheduleId = this.dataModel.getFirstAvailableSchedule();

    if (scheduleId !== undefined)
      this.resolveDataGrid().navigateToRow(scheduleId);
  }

  setProcessingSchedulesMessage() {
    this.updateDataGridEmptyMessage('Processing schedules...');
  }

  updateDataGridEmptyMessage(dataGridMessage) {
    if (StringUtils.isValidString(dataGridMessage))
      this.dataGridEmptyMessage = dataGridMessage;

    var dataGrid = this.resolveDataGrid();
    if (CoreUtils.isUndefinedOrNull(dataGrid))
      return;

    var noDataTextElement = dataGrid
      .element()
      .querySelector('.dx-datagrid-nodata');

    if (CoreUtils.isUndefinedOrNull(noDataTextElement))
      return;

    noDataTextElement.innerText = this.dataGridEmptyMessage;
  };

  getActiveDateFormatted() {
    return DateTimeUtils.formatMonthAndYear(
      this.commandDataModel.currentMonth,
      this.locale
    );
  }

  async enactGridFilters() {
    var dataGrid = this.resolveDataGrid();
    if (CoreUtils.isUndefinedOrNull(dataGrid))
      return;

    var requiredMonth = this.commandDataModel.currentMonth;

    switch (this.commandDataModel.dateViewMode) {
      case DateViewMode.fullMonth: {
        if (this.commandDataModel.showPastDays)
          dataGrid.clearFilter();
        else
          dataGrid.filter(['scheduleDate', '>=', this.commandDataModel.currentDate]);

        break;
      }
      case DateViewMode.weccPreschedule: {
        requiredMonth = await this.queryTargetPrescheduleMonth(this.commandDataModel.currentDate);

        if (this.commandDataModel.showPastDays)
          dataGrid.filter(['tradingDate', '<=', this.commandDataModel.currentDate]);
        else
          dataGrid.filter(['tradingDate', '=', this.commandDataModel.currentDate]);

        break;
      }
      case DateViewMode.weccRealtime: {
        requiredMonth = DateTimeUtils.getMonth(this.commandDataModel.currentDate);

        if (this.commandDataModel.showPastDays)
          dataGrid.filter(['scheduleDate', '<=', this.commandDataModel.currentDate]);
        else
          dataGrid.filter(['scheduleDate', '=', this.commandDataModel.currentDate]);

        break;
      }
      default:
        break;
    }

    var isRefreshRequired = false;
    if (!DateTimeUtils.areDatesEqual(this.commandDataModel.currentMonth, requiredMonth)) {
      this.commandDataModel.updateProperties({
        currentMonth: requiredMonth
      });

      isRefreshRequired = true;
    }

    this.setProcessingSchedulesMessage();
    this.scrollToFirstAvailableSchedule();

    return isRefreshRequired;
  }

  async queryTargetPrescheduleMonth(today) {
    var prescheduleDates = await ScheduleApi.getPreschedulePackageDates(
      this.counterparty.code,
      today
    );

    var dates = prescheduleDates.map(date => new Date(date));
    return dates.length > 0
      ? DateTimeUtils.getMonth(dates[0])
      : DateTimeUtils.getMonth(today);
  }

  async onGridFiltersChanged() {
    this.enactGridFilters().then(isRefreshRequired => {
      if (isRefreshRequired)
        this.refreshGridValues()
    });
  }

  async onDateRollingOver() {
    if (this.commandDataModel.isModified
      && this.commandDataModel.dateViewMode !== DateViewMode.fullMonth
      && !this.commandDataModel.showPastDays)
      this.revertGridChanges();

    this.enactGridFilters().then(isRefreshRequired => {
      if (isRefreshRequired)
        this.refreshGridValues()
    });
  }

  async onFullRefresh() {
    this.refreshGridValues();
  }

  async onBackgroundRefresh(lastRetrievalTimeUtc) {
    this.updateGridValues(lastRetrievalTimeUtc);
  }

  async onUpdateSchedules(scheduleUpdates) {
    await setTimeout(1);

    var dataGrid = this.resolveDataGrid();
    if (dataGrid === null)
      return;

    this.processScheduleUpdates(dataGrid, scheduleUpdates);
    dataGrid.refresh(true);
  }

  async onSubmitChanges() {
    this.submitGridChanges(
      this.onSubmitFinished.bind(
        this,
        'Schedule values were submitted successfully.',
        'An error occurred while submitting schedule values.'
      )
    );
  }

  async onSubmitFinished(successMessage, errorMessage, response) {
    if (response.status === 200) {
      var scheduleSubmissionResultsDto = await response.json();
      this.dataModel.processServerSubmitResult(scheduleSubmissionResultsDto.schedules);

      notify(successMessage, 'success', 3000);
    } else {
      notify(errorMessage, 'error', 3000);
    }
  }

  async onRevertChanges() {
    this.revertGridChanges();
  }

  async onExportToExcel() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(this.getActiveDateFormatted());

    exportDataGrid({
      worksheet: worksheet,
      component: this.resolveDataGrid(),
      customizeCell : function(options) {
          const { gridCell, excelCell } = options;
          if (gridCell.rowType === 'data' && gridCell.value instanceof EnergyValue) {
              excelCell.value = gridCell.value.value !== 0
				                  ? gridCell.value.value
				                  : gridCell.data.showZeroValues ? 0 : null;
	          //debugger;
          }
      }
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), this.counterparty.displayName + '.xlsx');
      });
    });
  }

  onResizeObserverEvent() {
    if (this.parentContainerElement === null)
      return;

    this.updateViewHeight();
  }

  updateViewHeight(dataGrid) {
    var viewHeight = this.calculateViewHeight();

    if (CoreUtils.isUndefinedOrNull(dataGrid))
      dataGrid = this.resolveDataGrid();

    if (dataGrid !== null)
      dataGrid.option('height', viewHeight - 50);

    DomUtils.setElementHeight(
      DomUtils.getElementItemByClassName('pwx-schedule-view'),
      viewHeight
    );

    this.updateDataGridEmptyMessage();
  }

  calculateViewHeight() {
    return DomUtils.calculateInnerContentHeight(
      DomUtils.getElementItemByClassName('pwx-main-container')
    );
  }

  attachKeyDownEvent() {
    var container = DomUtils.getElementItemByClassName('pwx-main-container');
    container.addEventListener('keydown', this.viewKeyDownHandler, false);
    container.focus();
  }

  detachKeyDownEvent() {
    var container = DomUtils.getElementItemByClassName('pwx-main-container');
    container.removeEventListener('keydown', this.viewKeyDownHandler, false);
  }

  onDataGridCellHoverChanged(event) {
    if (event.eventType === 'mouseover' && CoreUtils.isDefinedAndNotNull(event.column))
      this.onShowDataGridToolTip(event.cellElement, event.data, event.column.dataField, this.toolTipDelayMs);
    else if (event.eventType === 'mouseout')
      this.toolTipController.deactivate();
  }

  onDataGridCellPrepared(event) {
    if (event.rowType === 'group')
      return;

    if (event.rowType === 'data')
      this.isAltRowIndex[event.rowIndex] = event.isAltRow;

    this.updateCellAppearance(event.data, event.column.dataField, event.isAltRow, event.cellElement);
  }

  onDataGridContextMenuPreparing(event) {
    if (event.target !== 'content')
      return;

    event.items = [];
  }

  onDataGridInitialized(event) {
    this.updateViewHeight(event.component);
  }

  onDataGridContentReady(event) {
    if (CoreUtils.isDefinedAndNotNull(this.dataGridScrollable))
      this.dataGridScrollable.off('scroll', this.onDataGridScrollListener);

    this.dataGridScrollable = event.component.getScrollable();

    if (CoreUtils.isDefinedAndNotNull(this.dataGridScrollable))
      this.dataGridScrollable.on('scroll', this.onDataGridScrollListener);

    if (!this.hasDataGridInitiallyLoaded)
      return;

    this.onDataGridFinishedLoading();
  }

  onDataGridFinishedLoading() {
    if (this.isSwitchingViews) {
      this.isSwitchingViews = false;

      this.updateDataGridEmptyMessage();
      this.updateCurrentFilter(this.state.currentViewTab?.filter);
    } else {
      if (this.resolveDataGrid().totalCount() < 0)
        return;

      this.endOperationInProgress();
    }

    var formattedDate = this.getActiveDateFormatted();

    var dataGridMessage = this.resolveDataGrid().totalCount() === 0
      ? this.dataModel.viewSchedules.length === 0
        ? 'No electable schedules available for ' + formattedDate + '.'
        : 'Select \'Show Past Days\' to view previous schedules for ' + formattedDate + '.'
      : '';

    this.updateDataGridEmptyMessage(dataGridMessage);
  }

  onDataGridEditingStart(event) {
    if (!this.dataModel.canEditFieldValue(event.data, event.column.dataField)) {
      event.cancel = true;
      return;
    }

    if (this.dataModel.isElectableHourField(event.data, event.column.dataField)
      && this.dataModel.getHourFieldSubhourlyResolution(event.data) > 1
      && this.usePopOutEditor) {
      event.cancel = true;
      this.showPopOutEditor(event.data.scheduleId, event.column.dataField, 1, false);
      return;
    }

    this.currentEdit = {
      schedule: event.data.scheduleId,
      dataField: event.column.dataField
    };
  }

  onDataGridEditCanceling(event) {
    this.currentEdit = null;
  }

  onDataGridKeyDown(event) {
  }

  onDataGridOptionChanged(event) {
    var columnFieldInfo = GridUtils.parseColumnUpdateFieldInfo(event.fullName);
    if (CoreUtils.isUndefinedOrNull(columnFieldInfo))
      return;

    this.columnManager.settings.retrieveColumnSetting(
      event.component,
      columnFieldInfo.columnIndex,
      columnFieldInfo.dataField,
      event.value,
      event.previousValue
    );

    this.columnManager.settings.storeColumnSettings();
  }

  onDataGridRowPrepared(event) {
    var rowElement = event.rowElement;

    DomUtils.ensureClassNameExists(
      rowElement.classList,
      this.getDataGridRowClassName()
    );

    if (event.rowType !== 'data' || CoreUtils.isUndefinedOrNull(event.data))
      return;

    DomUtils.ensureClassNameExists(
      rowElement.classList,
      'pwx-display-normal'
    );

    var schedule = event.data;
    this.scheduleRowIndex[schedule.scheduleId] = rowElement;
    this.updateRowAppearance(schedule);
  }

  onDataGridScroll(event) {
    var popOutEditor = this.popOutEditorRef.current;
    if (CoreUtils.isUndefinedOrNull(popOutEditor))
      return false;

    popOutEditor.repositionEditor();
  }

  onDataGridRowUpdated(event) {
    this.dataModel.onFieldValuesEdited(event.data);
  }

  onDataGridRowUpdating(event) {
    if (!this.dataModel.onFieldValuesEditing(event.oldData, event.newData))
      event.cancel = true;
  }

  updateRowAppearance(schedule) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return;

    var rowElement = this.scheduleRowIndex[schedule.scheduleId];
    if (CoreUtils.isUndefinedOrNull(rowElement))
      return;

    DomUtils.replaceClassName(
      rowElement.classList,
      'pwx-display-',
      schedule.isGrayedOut ? 'pwx-display-grayed' : 'pwx-display-normal'
    );
  }

  updateCellAppearance(schedule, dataField, isAltRow, element) {
    if (CoreUtils.isUndefinedOrNull(schedule))
      return;

    const cellPrefix = 'pwx-cell-';
    const flagPrefix = 'pwx-flag-';
    const alertOverlayPrefix = 'pwx-alert-overlay-';
    const statePrefix = 'pwx-state-';
    const highlightPrefix = 'pwx-highlight';

    var hourTypeCode = this.dataModel.getHourTypeHighlightCode(schedule, dataField);
    if (hourTypeCode === hlhCode)
      DomUtils.replaceClassName(
        element.classList,
        highlightPrefix,
        'pwx-highlight-hlh'
      );
    else if (hourTypeCode === llhCode)
      DomUtils.replaceClassName(
        element.classList,
        highlightPrefix,
        'pwx-highlight-llh'
      );
    else if (hourTypeCode === atcCode)
      DomUtils.replaceClassName(
        element.classList,
        highlightPrefix,
        'pwx-highlight-atc'
      );
    else
      DomUtils.replaceClassName(
        element.classList,
        highlightPrefix,
        'pwx-highlight-none'
      );

    if ((dataField === hlhEntryField || dataField === llhEntryField || dataField === atcEntryField)
      && (!this.dataModel.isComputedField(schedule, dataField))) {
      DomUtils.replaceClassName(
        element.classList,
        statePrefix,
        isAltRow ? 'pwx-state-quick-entry-alt' : 'pwx-state-quick-entry'
      );
      return;
    }

    var alertValidation = this.dataModel.isFieldValueFailingValidation(schedule, dataField);
    var alertCutoff = this.dataModel.showFieldValueElectionCutoffAlert(schedule, dataField);

    if (this.dataModel.isFieldValueFlagged(schedule, dataField))
      DomUtils.replaceClassName(
        element.classList,
        flagPrefix,
        'pwx-flag-active'
      );
    else
      DomUtils.replaceClassName(
        element.classList,
        flagPrefix,
        'pwx-flag-none'
      );

    if (this.dataModel.isComputedField(schedule, dataField))
      DomUtils.replaceClassName(
        element.classList,
        cellPrefix,
        'pwx-cell-computed'
      );
    else
      DomUtils.replaceClassName(
        element.classList,
        cellPrefix,
        'pwx-cell-standard'
      );

    if (alertValidation && alertCutoff)
      DomUtils.replaceClassName(
        element.classList,
        alertOverlayPrefix,
        'pwx-alert-overlay-validation-cutoff'
      );
    else if (alertValidation)
      DomUtils.replaceClassName(
        element.classList,
        alertOverlayPrefix,
        'pwx-alert-overlay-validation'
      );
    else if (alertCutoff)
      DomUtils.replaceClassName(
        element.classList,
        alertOverlayPrefix,
        'pwx-alert-overlay-cutoff'
      );
    else
      DomUtils.replaceClassName(
        element.classList,
        alertOverlayPrefix,
        'pwx-alert-overlay-none'
      );

    if (this.dataModel.isComputedField(schedule, dataField))
      DomUtils.replaceClassName(
        element.classList,
        statePrefix,
        isAltRow ? 'pwx-state-computed-alt' : 'pwx-state-computed'
      );
    else if (this.dataModel.isFieldValueModified(schedule, dataField))
      DomUtils.replaceClassName(
        element.classList,
        statePrefix,
        isAltRow ? 'pwx-state-modified-alt' : 'pwx-state-modified'
      );
    else if (this.dataModel.isFieldValueOverridden(schedule, dataField))
      DomUtils.replaceClassName(
        element.classList,
        statePrefix,
        isAltRow ? 'pwx-state-overridden-alt' : 'pwx-state-overridden'
      );
    else if (this.dataModel.canElectFieldValue(schedule, dataField))
      DomUtils.replaceClassName(
        element.classList,
        statePrefix,
        isAltRow ? 'pwx-state-electable-alt' : 'pwx-state-electable'
      );
    else
      DomUtils.replaceClassName(
        element.classList,
        statePrefix,
        isAltRow ? 'pwx-state-normal-alt' : 'pwx-state-normal'
      );
  }

  canEditHourlyField(scheduleId, dataField) {
    return this.dataModel.isElectableHourField(
      this.dataModel.resolveScheduleById(scheduleId),
      dataField
    );
  }

  onShowDataGridToolTip(targetElement, schedule, dataField, toolTipDelayMs) {
    if (schedule === null)
      return false;

    var toolTipInfo = this.dataModel.getFieldValueToolTipInfo(schedule, dataField);
    if (toolTipInfo !== null) {
      this.toolTipController.activate(
        targetElement,
        toolTipInfo.contentRender(),
        toolTipInfo.position,
        toolTipDelayMs
      );

      return true;
    }

    return false;
  }

  onPopOutEditorFocusBackward(scheduleId, dataField, currentResolution) {
    var previousDataField = this.dataModel.getPreviousHourField(scheduleId, dataField);
    if (previousDataField !== null && this.canEditHourlyField(scheduleId, previousDataField))
      this.showPopOutEditor(scheduleId, previousDataField, currentResolution, true);
  }

  onPopOutEditorFocusForward(scheduleId, dataField, currentResolution) {
    var nextDataField = this.dataModel.getNextHourField(scheduleId, dataField);
    if (nextDataField !== null && this.canEditHourlyField(scheduleId, nextDataField))
      this.showPopOutEditor(scheduleId, nextDataField, currentResolution, false)
  }

  onPopOutEditorResolvePivotElements(scheduleId, dataField) {
    var dataGrid = this.resolveDataGrid();
    if (dataGrid === null)
      return;

    var rowIndex = dataGrid.getRowIndexByKey(scheduleId);
    if (rowIndex === -1)
      return null;

    return {
      cellElement: dataGrid.getCellElement(rowIndex, dataField),
      scrollableParentElement: this.dataGridScrollable.element()
    };
  }

  showPopOutEditor(scheduleId, dataField, previousResolution, focusOnLastValue) {
    AsyncUtils.delayAction(() =>
      this.showPopOutEditorAsync(scheduleId, dataField, previousResolution, focusOnLastValue),
      20
    );
  }

  showPopOutEditorAsync(scheduleId, dataField, previousResolution, focusOnLastValue) {
    var dataGrid = this.resolveDataGrid();
    if (CoreUtils.isUndefinedOrNull(dataGrid))
      return false;

    var popOutEditor = this.popOutEditorRef.current;
    if (CoreUtils.isUndefinedOrNull(popOutEditor))
      return false;

    popOutEditor.showEditor(
      scheduleId,
      dataField,
      previousResolution,
      focusOnLastValue
    );

    return true;
  }

  closePopOutEditor() {
    var popOutEditor = this.popOutEditorRef.current;
    if (CoreUtils.isDefinedAndNotNull(popOutEditor))
      popOutEditor.closeEditor();
  }

  disposePopOutEditor() {
    var popOutEditor = this.popOutEditorRef.current;
    if (CoreUtils.isDefinedAndNotNull(popOutEditor))
      popOutEditor.dispose();
  }

  processScheduleUpdates(dataGrid, scheduleUpdates) {
    if (CoreUtils.isUndefinedOrNull(scheduleUpdates) || !Array.isArray(scheduleUpdates))
      return;

    scheduleUpdates.forEach(scheduleUpdate => {
      var rowIndex = dataGrid.getRowIndexByKey(scheduleUpdate.scheduleId);
      if (rowIndex === -1)
        return;

      var schedule = this.dataModel.resolveScheduleById(scheduleUpdate.scheduleId);

      this.updateRowAppearance(schedule);

      scheduleUpdate.dataFields.forEach(dataField => {
        var cellElement = dataGrid.getCellElement(rowIndex, dataField);
        if (CoreUtils.isUndefinedOrNull(cellElement))
          return;

        this.updateCellAppearance(
          schedule,
          dataField,
          this.isAltRowIndex[rowIndex],
          cellElement
        );

        this.cancelActiveEditsIfNecessary(dataGrid, schedule, dataField);
      });

      dataGrid.repaintRows([rowIndex]);
    });
  }

  cancelActiveEditsIfNecessary(dataGrid, schedule, dataField) {
    if (CoreUtils.isUndefinedOrNull(this.currentEdit))
      return;

    if (this.currentEdit.scheduleId !== schedule.scheduleId
      || this.currentEdit.dataField !== dataField)
      return;

    if (!this.dataModel.canEditFieldValue(schedule, dataField))
      dataGrid.cancelEditData();
  }

  onShowManageColumnPreferences() {
    if (!this.commandDataModel.canUserManageColumnPreferences)
      return;

    this.manageColumnDefaultsDataModel.updateProperties({
      popupSessionId: crypto.randomUUID(),
      popupTitle: 'Manage Default Column Preferences',
      popupVisible: true,
      counterpartyName: this.counterparty.displayName,
      columnSettings: this.columnManager.settings
    });
  }

  onManageColumnDefaultsPopupCancel() {
    this.manageColumnDefaultsDataModel.updateProperties({
      popupVisible: false
    });
  }

  onResubmitValues(){
    this.dateRangeDataModel.updateProperties({
      popupSessionId: crypto.randomUUID(),
      popupMode: DateRangePopupMode_Resubmit,
      popupTitle: 'Resubmit Values',
      popupVisible: true,
      availableContracts: Array.from(this.commandDataModel.resubmitContracts),
      selectedContracts: Array.from(this.commandDataModel.resubmitContracts),
      rangeFirstDate: this.commandDataModel.currentMonth,
      rangeLastDate: DateTimeUtils.nudgeDateToLastDayOfMonth(this.commandDataModel.currentMonth),
      submitPrinceTrades: true,
      submitSata: true,
      requireSubmissionType: true,
      commandType: 'danger',
      commandLabel: 'Resubmit',
      commandIcon: 'upload'
    });
  }

  onRecalculateValues() {
    this.dateRangeDataModel.updateProperties({
      popupSessionId: crypto.randomUUID(),
      popupMode: DateRangePopupMode_Recalculate,
      popupTitle: 'Recalculate Values',
      popupVisible: true,
      availableContracts: Array.from(this.commandDataModel.recalculateContracts),
      selectedContracts: Array.from(this.commandDataModel.recalculateContracts),
      rangeFirstDate: this.commandDataModel.currentMonth,
      rangeLastDate: DateTimeUtils.nudgeDateToLastDayOfMonth(this.commandDataModel.currentMonth),
      submitPrinceTrades: true,
      submitSata: false,
      requireSubmissionType: false,
      commandType: 'default',
      commandLabel: 'Recalculate',
      commandIcon: 'datafield'
    });
  }

  onDateRangePopupContinue() {
    this.dateRangeDataModel.updateProperties({
      popupVisible: false
    });

    switch (this.dateRangeDataModel.popupMode) {
      case DateRangePopupMode_Resubmit:
        ScheduleApi.resubmit(
          this.counterparty.code,
          this.dateRangeDataModel.selectedContracts.map(contract => contract.code),
          this.dateRangeDataModel.rangeFirstDate,
          this.dateRangeDataModel.rangeLastDate,
          this.dateRangeDataModel.submitPrinceTrades,
          this.dateRangeDataModel.submitSata,
          this.onSubmitFinished.bind(
            this,
            'Schedule values were resubmitted successfully.',
            'An error occurred while resubmitting schedule values.'
          )
        );
        break;
      case DateRangePopupMode_Recalculate:
        ScheduleApi.recalculate(
          this.counterparty.code,
          this.dateRangeDataModel.selectedContracts.map(contract => contract.code),
          this.dateRangeDataModel.rangeFirstDate,
          this.dateRangeDataModel.rangeLastDate,
          this.dateRangeDataModel.submitPrinceTrades,
          this.dateRangeDataModel.submitSata,
          this.onSubmitFinished.bind(
            this,
            'Schedule values were recalculated successfully.',
            'An error occurred while recalculating schedule values.'
          )
        );
        break;
      default:
        break;
    }
  }

  onDateRangePopupCancel() {
    this.dateRangeDataModel.updateProperties({
      popupVisible: false
    });
  }
}